<template>
  <div class="reviews-block grid grid-cols-12 gap-4 max-md:gap-6">
    <div class="col-span-7 max-xl:col-span-6 max-lg:col-span-12 max-lg:order-2">
      <div v-if="pending" class="space-y-2">
        <SPSkeleton
          v-for="(_, index) in 4"
          :key="index"
          variant="card"
          :ui="{
            card: 'w-full h-34',
          }"
        />
      </div>

      <div
        v-if="!items?.length"
        class="h-full max-lg:min-h-50 flex flex-col items-center justify-center text-center bg-white rounded-[16px]"
      >
        <p class="typo-paragraph-md-bold text-neutral-default mb-2">
          Отзывов пока нет
        </p>
        <p class="typo-paragraph-sm-regular text-neutral-light">
          Будьте первыми, оставьте отзыв заполнив форму
        </p>
      </div>

      <div v-else class="space-y-2">
        <UCard
          v-for="(review, index) in items"
          :key="index"
          class="ring-0"
          :ui="{
            body: 'p-5',
          }"
        >
          <UInputRating
            v-model="review.rating"
            class="mb-3 pointer-events-none"
            empty-icon="i-local-empty-star"
            icon="i-ph-star-fill"
            color="warning"
            size="sm"
          />

          <p
            class="typo-paragraph-md-medium text-content-default-secondary mb-5"
          >
            {{ review.content }}
          </p>

          <div
            class="flex justify-between items-center typo-paragraph-xs-medium"
          >
            <div class="text-content-default">
              {{ review.author_name }}
            </div>

            <div class="text-content-default-secondary">
              {{ dayjs(review.created_at).format('DD.MM.YYYY') }}
            </div>
          </div>
        </UCard>
      </div>
    </div>

    <div
      class="col-span-5 max-xl:col-span-6 max-lg:col-span-12 space-y-2 max-lg:order-1"
    >
      <UCard
        v-if="!pending && items.length"
        class="ring-0 max-md:mb-4"
        :ui="{
          body: 'p-6',
        }"
      >
        <div class="flex items-center justify-between">
          <span class="typo-heading-h4 text-content-default">
            Рейтинг: {{ totalRating }} / 5
          </span>

          <div class="flex items-center gap-3">
            <UInputRating
              v-if="totalRating"
              :model-value="Math.round(totalRating * 10) / 10"
              class="pointer-events-none"
              empty-icon="i-local-empty-star"
              icon="i-ph-star-fill"
              color="warning"
              size="lg"
              :step="0.1"
              :ui="{
                item: 'h-5.25 w-5.25 max-md:h-4 max-md:w-4!',
                icon: 'h-5.25 w-5.25 max-md:h-4 max-md:w-4',
              }"
            />
            <span
              v-if="amount"
              class="typo-paragraph-md-regular text-content-default-secondary"
            >
              {{ ratingAmountText(amount) }}
            </span>
          </div>
        </div>
      </UCard>

      <UCard
        class="ring-0 max-lg:hidden"
        :title="title"
        :description="description"
        :ui="{
          header: 'p-8 pb-6',
          body: 'p-8 pt-0!',
          title: 'typo-heading-h3 text-content-default',
          description:
            'typo-paragraph-sm-regular text-content-default-secondary',
        }"
      >
        <ReviewForm :api-method="apiMethod" />
      </UCard>

      <UCard
        class="ring-0! lg:hidden w-full flex max-md:flex-wrap justify-between items-center p-4"
        :title="title"
        :description="description"
        :ui="{
          header: 'max-lg:p-0!',
          body: 'max-lg:p-0! max-md:w-full',
          title: 'typo-paragraph-md-medium text-content-default',
          description:
            'typo-paragraph-sm-regular text-content-default-secondary max-md:max-w-8/10',
        }"
      >
        <UButton
          class="max-md:w-full max-md:mt-4"
          size="md"
          @click="openReviewModal"
        >
          Оставить отзыв
        </UButton>
      </UCard>

      <UCard
        class="snackbar hidden w-full shadow max-md:flex-wrap justify-between items-center p-4 fixed left-0 z-100 bottom-0 rounded-bl-none rounded-br-none"
        :class="{
          'is-visible': isSnackbarVisible,
        }"
        :title="title"
        :description="description"
        :ui="{
          header: 'max-lg:p-0! w-full',
          body: 'max-lg:p-0! max-md:w-full',
          title: 'typo-heading-h4 text-content-default',
          description:
            'typo-paragraph-sm-regular text-content-default-secondary',
        }"
      >
        <UButton
          class="grow-0 w-max ml-auto max-md:w-full max-md:mt-4"
          size="md"
          @click="openReviewModal"
        >
          Оставить отзыв
        </UButton>
      </UCard>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Review, StoreReview } from '~/api'
import { declOfNumWithVal } from '~/shared'
import { dayjs } from '~/shared/day'
import ReviewModal from './modals/ReviewModal.vue'
import ReviewForm from './forms/ReviewForm.vue'

defineOptions({
  name: 'ReviewsBlock',
})

const props = withDefaults(
  defineProps<{
    items: Review[]
    totalRating?: number | null
    amount?: number
    title?: string
    description?: string
    apiMethod: (id: string, data: StoreReview) => Promise<void>
    pending?: boolean
  }>(),
  {
    title: 'Оставить отзыв',
    description: 'Отзыв будет опубликован после проверки модератором',
  },
)

const overlay = useOverlay()
const reviewModal = overlay.create(ReviewModal)

const isSnackbarVisible = ref(false)

const ratingAmountText = (amount: number) =>
  declOfNumWithVal(amount, ['оценка', 'оценки', 'оценок'])

const openReviewModal = () => {
  reviewModal.open({
    apiMethod: props.apiMethod,
    title: props.title,
    description: props.description,
  })
}

const checkSnackbarVisibility = () => {
  isSnackbarVisible.value = window.scrollY > 950
}

onMounted(() => {
  if (window.innerWidth < 1024) {
    window.addEventListener('scroll', checkSnackbarVisibility)
  }
})

onBeforeUnmount(() => {
  if (import.meta.client) {
    window.removeEventListener('scroll', checkSnackbarVisibility)
  }
})
</script>

<style lang="scss" scoped>
.reviews-block {
  --ui-warning: var(--color-warning-muted);
}

.snackbar {
  &.is-visible {
    display: flex;
  }
}
</style>
